var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { Children, useEffect } from 'react';
import CarouselContext from './private/utils/CarouselContext';
import useCarouselContext from './private/utils/useCarouselContext';
import useInterval from './private/utils/useInterval';
import withHorizontalScroll from './private/utils/withHorizontalScroll';
import IconButton from '../iconButton/IconButton';
import mergeClassnames from '../mergeClassnames/mergeClassnames';
const CarouselRoot = (_a) => {
    var { children, scrollTo, className, step, selectedIndex, autoSlideDelay, isRtl } = _a, rest = __rest(_a, ["children", "scrollTo", "className", "step", "selectedIndex", "autoSlideDelay", "isRtl"]);
    const { itemRef, scrollLeftToStep, scrollRightToStep, containerRef, canScrollLeft, canScrollRight, scrollToIndex, itemsCount, firstVisibleIndex, lastVisibleIndex, } = withHorizontalScroll({
        scrollStep: step || 5,
        scrollTo: scrollTo,
        scrollInContainer: true,
        isRtl,
    });
    useInterval(() => {
        if (!autoSlideDelay)
            return;
        if (isRtl) {
            if (canScrollLeft) {
                scrollLeftToStep();
            }
            else {
                scrollToIndex(itemsCount - 1);
            }
        }
        else {
            if (canScrollRight) {
                scrollRightToStep();
            }
            else {
                scrollToIndex(0);
            }
        }
    }, autoSlideDelay);
    useEffect(() => {
        if (selectedIndex !== undefined) {
            scrollToIndex(isRtl ? itemsCount - selectedIndex - 1 : selectedIndex);
        }
    }, [selectedIndex, isRtl, itemsCount]);
    return (React.createElement(CarouselContext.Provider, { value: {
            itemRef,
            scrollLeftToStep,
            scrollRightToStep,
            canScrollLeft,
            canScrollRight,
            containerRef,
            selectedIndex,
            itemsCount,
            firstVisibleIndex,
            lastVisibleIndex,
            autoSlideDelay,
            isRtl,
        } },
        React.createElement("div", Object.assign({ className: mergeClassnames('relative w-full', className) }, rest), typeof children === 'function'
            ? children({
                scrollLeftToStep,
                scrollRightToStep,
                canScrollLeft,
                canScrollRight,
                firstVisibleIndex,
                lastVisibleIndex,
            })
            : children)));
};
const Reel = (_a) => {
    var { children, className } = _a, rest = __rest(_a, ["children", "className"]);
    const { containerRef, autoSlideDelay, isRtl } = useCarouselContext('Carousel.Reel');
    const arrayChildren = Children.toArray(children);
    const revertChildren = arrayChildren.reverse();
    return (React.createElement("ul", Object.assign({ className: mergeClassnames('flex overflow-x-auto overflow-y-hidden h-auto hidden-scroll gap-4', '[-webkit-overflow-scrolling:touch] [scrollbar-width:none]', '[-ms-overflow-style:-ms-autohiding-scrollbar]', '[&>li]:list-none [&>li]:before:absolute [&>li]:before:content-["\\200B"]', '[&>*]:flex-[0_0_auto] [&>img]:h-full [&>img]:basis-auto [&>img]:w-auto', 'snap-x snap-mandatory rtl:flex-row-reverse', autoSlideDelay && 'overflow-x-hidden', className), ref: containerRef }, rest), isRtl ? revertChildren : children));
};
// TODO: highlight selected item (mark it as selected)
const Item = (_a) => {
    var { children, className } = _a, rest = __rest(_a, ["children", "className"]);
    const { itemRef } = useCarouselContext('Carousel.Item');
    return (React.createElement("li", Object.assign({ className: mergeClassnames('snap-center flex bg-goku rounded-moon-s-sm items-center justify-center', className), ref: itemRef }, rest), children));
};
const LeftArrow = (_a) => {
    var { children, className } = _a, rest = __rest(_a, ["children", "className"]);
    const { scrollLeftToStep = () => { }, canScrollLeft } = useCarouselContext('Carousel.LeftArrow');
    return (React.createElement(IconButton, Object.assign({ size: "sm", className: mergeClassnames('max-sm:hidden shadow-moon-sm bg-goku text-bulma', 'absolute top-1/2 -translate-y-1/2 origin-[top_center] z-5 -start-4', className), onClick: scrollLeftToStep, disabled: !canScrollLeft, "aria-label": "Scroll left" }, rest), children));
};
const RightArrow = (_a) => {
    var { children, className } = _a, rest = __rest(_a, ["children", "className"]);
    const { scrollRightToStep = () => { }, canScrollRight } = useCarouselContext('Carousel.RightArrow');
    return (React.createElement(IconButton, Object.assign({ size: "sm", className: mergeClassnames('max-sm:hidden shadow-moon-sm bg-goku text-bulma', 'absolute top-1/2 -translate-y-1/2 origin-[top_center] z-5 -end-4', className), onClick: scrollRightToStep, disabled: !canScrollRight, "aria-label": "Scroll right" }, rest), children));
};
// TODO: add slide indicator timer
const Indicators = (_a) => {
    var { className } = _a, rest = __rest(_a, ["className"]);
    const { itemsCount, selectedIndex, firstVisibleIndex = 0, lastVisibleIndex = 0, } = useCarouselContext('Carousel.RightArrow');
    const activeIndex = !selectedIndex ||
        selectedIndex < firstVisibleIndex ||
        selectedIndex > lastVisibleIndex
        ? firstVisibleIndex
        : selectedIndex;
    const items = Array.from({ length: itemsCount !== null && itemsCount !== void 0 ? itemsCount : 0 }, (index) => index);
    return (React.createElement("div", Object.assign({ className: mergeClassnames('flex absolute bottom-8 left-1/2 -translate-x-1/2 rtl:flex-row-reverse', className) }, rest), items === null || items === void 0 ? void 0 : items.map((_, index) => (React.createElement("div", { className: mergeClassnames('w-2 h-2 mx-1 rounded-full bg-beerus', activeIndex === index && 'bg-piccolo'), key: index })))));
};
const Carousel = Object.assign(CarouselRoot, {
    Reel,
    LeftArrow,
    RightArrow,
    Item,
    Indicators,
});
export default Carousel;
